import { Download } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly, formatDateTime, toIso } from '../../../../lib/formatDate';
import { saveBlob } from '../../../../lib/saveBlob';
import type { MySurveyTraining, TrainingReportAttachment } from '../mySurveyTypes';

/**
 * ONE completed training's report, on Preview › Report — the plan's own training, or a training
 * recommendation's: the same card, so a report reads one way wherever its training came from
 * (product call, 13 Oct 2026: the report lives on the Report sub-tab and nowhere else).
 *
 * **Submit and Edit are the card's own** — Edit behind the STANDARD settings cog once a report is
 * in (disabled, with the reason, while none is), and Submit offered in the card's empty state. Both open the caller's report form, on the
 * key of what it writes. No cog and no button where `onEdit` is absent (the survey is deleted).
 */
export function MySurveyTrainingReportCard({
  id,
  training,
  onEdit,
  fetchAttachment,
}: {
  /** The card's DOM id — unique per training. */
  id: string;
  /** A COMPLETED training; its report may or may not be in. */
  training: MySurveyTraining;
  /** Opens the report form for THIS training. Absent when a report cannot be written. */
  onEdit?: () => void;
  /** Fetches one attached file — the endpoint differs by where the training came from. */
  fetchAttachment: (attachmentId: string) => Promise<{ blob: Blob; fileName?: string | null }>;
}) {
  const toast = useToast();
  // The id of the file being fetched; null while none is.
  const [downloading, setDownloading] = useState<string | null>(null);

  // `!= null`: it came off the wire, where an absent value is undefined.
  const reported = training.reportAt != null;
  const attachments = training.reportAttachments ?? [];

  async function download(attachment: TrainingReportAttachment) {
    setDownloading(attachment.id);

    try {
      const { blob, fileName } = await fetchAttachment(attachment.id);
      saveBlob(blob, fileName ?? attachment.fileName);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not download the attached file.');
    } finally {
      setDownloading(null);
    }
  }

  /** One titled list of files, each with its Download — an em dash where the report carries none of that kind. */
  const fileSection = (title: string, files: TrainingReportAttachment[]) => (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-slate-800">{title}</h3>
      {files.length === 0 ? (
        <p className="text-sm text-slate-400">—</p>
      ) : (
        <ul className="flex flex-col divide-y divide-slate-200 rounded-lg border border-slate-200">
          {files.map((attachment) => (
            <li key={attachment.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0 break-all text-sm text-slate-600">{attachment.fileName}</span>
              <Button
                id={`${id}-download-${attachment.id}`}
                variant="secondary"
                leading={<Download className="h-4 w-4" aria-hidden="true" />}
                onClick={() => void download(attachment)}
                loading={downloading === attachment.id}
                loadingLabel="Downloading"
              >
                Download
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <ProfileCard
      id={id}
      title={training.title}
      onEdit={onEdit}
      editPermission="my-surveys.training"
      // NOTHING TO EDIT until a report is in (product call, 13 Oct 2026): the cog's Edit is disabled
      // for that STATE, with the reason. Submitting the first one is the empty state's own button.
      editDisabledReason={reported ? null : 'No report has been submitted yet. Submit one first.'}
      padded
      // The training is done and no report is in: say so, and offer the way in — the card's own Edit.
      empty={
        reported
          ? undefined
          : {
              title: 'No report yet',
              description: 'This training is completed. Submit your report on it, with any certificates or documents.',
              actionLabel: 'Submit report',
            }
      }
    >
      <div className="flex flex-col gap-4">
        <PreviewTable
          variant="table-display"
          rows={[
            // Calendar dates — `formatDateOnly`, never the instant path.
            { label: 'Start date', value: formatDateOnly(training.startDate), mono: true },
            { label: 'End date', value: formatDateOnly(training.endDate), mono: true },
            {
              label: 'Report submitted',
              value: reported ? (
                <time dateTime={toIso(training.reportAt!)} className="font-mono">
                  {formatDateTime(training.reportAt!)}
                </time>
              ) : null,
            },
          ]}
        />

        {/* In the ORDER the report form asks them (product call, 13 Oct 2026): the report, then its certificates and attachments. */}
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-slate-800">Report</h3>
          {/* The one renderer of stored markup — safe only because the server sanitised it on write. */}
          <RichTextDocument html={training.report ?? null} />
        </section>

        {/* THE FILES, one row each, in the form's own two fields: the certificates, then the rest. */}
        {fileSection('Certificates', attachments.filter((attachment) => attachment.isCertificate === true))}
        {fileSection('Other Attachments', attachments.filter((attachment) => attachment.isCertificate !== true))}
      </div>
    </ProfileCard>
  );
}
